import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { LoginModel } from '@app/models/login.model';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { Logout, SetToken } from '@app/store/auth/auth.classes';
import { HideSpinner, ShowSpinner } from '@app/store/spinner/spinner.classes';
import { Store } from '@ngxs/store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DialogService } from './dialog.service';
import { WebviewService } from './webview.service';
import { SettingSelectors } from '@app/store/setting/setting.selectors';
import { NameLocalStorage } from '@app/models/record.model';
import { jwtDecode, JwtPayload } from "jwt-decode";

interface Refresh {
  accessToken: string;
  refreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(
    private http: HttpClient,
    private store: Store,
    private dialog: DialogService,
    private ngZone: NgZone,
    private webviewService: WebviewService
  ) {}

  checkCorpPrefix() {
    return;
    // const url = `${environment.BUDDYCONSOLE}/auth/check-exists-corpprefix/${corpPrefix}`;
    // // const url = `${environment.KIDS_CLIENT_API}/auth/check-exists-corpprefix/${corpPrefix}`;
    // this.store.dispatch(new ShowSpinner());
    // const customHeadersOpt = {
    //    ...this.httpOptions, 
    //    headers: new HttpHeaders({ 
    //     'Content-Type': 'application/json',
    //     'ModuleId': '22'
    //   }),
    // };
    // return this.http.get<any>(url, customHeadersOpt).pipe(
    //   catchError((error: HttpErrorResponse) => {
    //     const message =
    //       error.status === 404
    //         ? 'ページが見つかりません'
    //         : 'サービスが利用できません';
    //     this.ngZone.run(() => this.dialog.message(message));
    //     return EMPTY;
    //   }),
    //   finalize(() => this.store.dispatch(new HideSpinner()))
    // );
  }

  login(payload: LoginModel): Observable<any> {
    localStorage.removeItem(NameLocalStorage.DISPLAY_SETTING_DATE);
    localStorage.removeItem(NameLocalStorage.CONDITION_FILTER);
    const url = `${environment.KIDS_CLIENT_API}/token/staff-auth/KIDS`;

    this.store.dispatch(new ShowSpinner());
    return this.http.post<any>(url, payload, this.httpOptions).pipe(
      tap((result: { accessToken?: string }) => {
        if (result?.accessToken) {
          localStorage.setItem('corpPrefix', payload.corpPrefix);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const message =
          error.status === 401
            ? 'ログインIDまたはパスワードが\n一致しません'
            : 'ログインできません';
        this.ngZone.run(() => this.dialog.message(message));
        if (error.status === 401) {
          this.webviewService.kidsExpireToken();
        }
        return EMPTY;
      }),
      finalize(() => this.store.dispatch(new HideSpinner()))
    );
  }

  checkStaffCode(accessToken: string = ''): Observable<any> {
    const url = `${environment.KIDS_CLIENT_API}/checkstaffcode/isauthaccount`;
    this.store.dispatch(new ShowSpinner());
    const httpOptions = this.httpOptions;
    httpOptions.headers = httpOptions.headers.set(
      'Authorization',
      accessToken
    );
    return this.http.get<any>(url, httpOptions).pipe(
      tap((res: boolean) => {
        if (!res) {
          localStorage.removeItem('corpPrefix');
          const message = 'ログインできません';
          this.ngZone.run(() => this.dialog.message(message));
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const message =
          error.status === 401
            ? 'ログインIDまたはパスワードが\n一致しません'
            : 'ログインできません';
        this.ngZone.run(() => this.dialog.message(message));
        if (error.status === 401) {
          this.webviewService.kidsExpireToken();
        }
        return EMPTY;
      }),
      finalize(() => this.store.dispatch(new HideSpinner()))
    );
  }

  refresh(
    accessToken: string | null,
    refreshToken: string | null
  ): Observable<Refresh> {
    if (!accessToken || !refreshToken) return EMPTY;
    const url = `${environment.TOKEN_API}/token/refresh`;
    const options = {
      headers: new HttpHeaders({
        Authorization: accessToken,
        'Content-Type': 'application/json',
      }),
    };

    //priority token change
    // const isWebView = this.store.selectSnapshot(SettingSelectors.isWebView);
    // const webviewAuth = this.getWebViewObject();

    this.store.dispatch(new ShowSpinner());
    return this.http.post<Refresh>(url, { refreshToken }, options).pipe(
      catchError(() => {
        this.store
          .dispatch(new Logout())
          .subscribe(() => {
            let webview = this.store.selectSnapshot(SettingSelectors.isWebView);
            if (webview) {
              this.webviewService.kidsExpireToken();
            } else {
              this.ngZone.run(() => {
                this.dialog.message('再度ログインしてください')
              }
              )
            }
          }
          );
        return EMPTY;
      }),
      finalize(() => this.store.dispatch(new HideSpinner()))
    );
  }

  deleteToken(accessToken: string | null): void {
    if (!accessToken) return;

    const url = `${environment.KIDS_CLIENT_API}/token/logout`;
    const body = {
      accessToken
    }

    this.http.post<void>(url, body).subscribe();
  }

  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) return false;
    let tokenCheck = !this.isTokenExpired(accessToken);
    if (!tokenCheck) {
      this.webviewService.kidsExpireToken();
    }
    return tokenCheck;
  }

  getAccessToken() {
    const isWebView = this.store.selectSnapshot(SettingSelectors.isWebView);
    const _accessToken = this.store.selectSnapshot(AuthSelectors.accessToken);
    const _webviewTokenObject = this.getWebViewObject();
    if (!isWebView) return _accessToken;
    if (!_webviewTokenObject) return _accessToken;
    const isWebviewTokenExpired = this.isTokenExpired(_webviewTokenObject.accessToken);

    if (!isWebviewTokenExpired) {
      const { accessToken, refreshToken } = _webviewTokenObject;
      this.store.dispatch(new SetToken((accessToken || ''), (refreshToken || '')))
      return accessToken;
    }
    return _accessToken;
  }

  private getWebViewObject(): { accessToken?: string; refreshToken?: string } | null {
    const _webviewTokenObject = localStorage.getItem('webview.token') || '';
    if (_webviewTokenObject === '') return null;
    return JSON.parse(_webviewTokenObject);
  }

  private absolutePath(url: string) {
    return url.split('(')[0].split('?')[0];
  }

  compRoleCheck(url: string) {
    if (url === '') return false;
    let absolutePath = this.absolutePath(url);
    const menu = this.store.selectSnapshot(SettingSelectors.menuList);
    const findMenu = menu.flatMap(x => x.menus).find(x => x.chkTtls?.length && x.chkTtls?.length > 0 &&
      x.chkTtls?.some(path => path.path === absolutePath) || x.path === absolutePath);
    return (findMenu && findMenu.write || false);
  }

  private isTokenExpired(token?: string): boolean {
    if (!token) return true;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const exp = decoded.exp;
      if (!exp) return false;

      const now = Math.floor(Date.now() / 1000);
      return now >= exp;
    } catch {
      return true;
    }
  }
}
